import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { ad_quote } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { quotesPageRouteLoader } from '@/routes/quotes/QuotesPage.route'
import { useScreenSize } from '@/themes/media-query'
import { Accordion, Item as AccordionItem } from 'devextreme-react/accordion'
import ODataStore from 'devextreme/data/odata/store'
import { exportDataGrid } from 'devextreme/excel_exporter'
import {
  Column,
  ColumnChooser,
  DataGrid,
  Editing,
  Export,
  FilterRow,
  HeaderFilter,
  Item,
  Scrolling,
  SearchPanel,
  Selection,
  StateStoring,
  Toolbar,
  DataGridTypes,
  MasterDetail,
  DataGridRef,
  Sorting,
  Search,
  FilterPanel,
  Pager,
  Button as GridButton,
} from 'devextreme-react/data-grid'
import notify from 'devextreme/ui/notify'
import { Workbook } from 'exceljs'
import saveAs from 'file-saver'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFetcher, useNavigate, useParams } from 'react-router-dom'
import { useLoaderData } from 'react-router-typesafe'
import { ClickEvent } from 'devextreme/ui/button'
import dxDataGrid, {
  CellPreparedEvent,
  ColumnButtonClickEvent,
  ExportingEvent,
  RowDblClickEvent,
} from 'devextreme/ui/data_grid'
import { GridCellColor } from '@/enums'
import { ContentReadyEvent } from 'devextreme/ui/accordion'
import { confirm } from 'devextreme/ui/dialog'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { AxiosError, AxiosResponse } from 'axios'
import { FormFilterItemDescriptorType } from '@/components/filter-form/GenericFilterForm.types'
import GenericFilterForm from '@/components/filter-form/GenericFilterForm'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import { EdmLiteral } from 'devextreme/data/odata/utils'
import { QuoteBriefDetail } from '@/routes/quotes/QuoteBriefDetail'
import { columnSourceFactory, isUserRoleAllowed, onDataGridSelectedRowButtonClick } from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import DateColumn from '@/components/date-column/DateColumn'
import { gridTotalItemsLabel } from '@/constants'
import DocumentHead from '@/components/document-head/DocumentHead'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { PopupRef } from 'devextreme-react/cjs/popup'
import { CommissionsSelector } from '@/routes/quotes/CommissionsSelector'
import { exportQuotePdf } from '@/routes/quotes/utils'

const QuotesPage = () => {
  const { title, aziende, userInfo, defaultCRUDAllowedRoles } = useLoaderData<typeof quotesPageRouteLoader>()
  const token = useTokenRefresh()

  const { client } = useQsAdminApiManager()

  const { clientId } = useParams()
  const fetcher = useFetcher()

  const currentScreenSize = useScreenSize()
  const gridRef = useRef<DataGridRef>(null)
  const commissionsPopupRef = useRef<PopupRef>(null)
  const navigate = useNavigate()

  const getFiltersConfiguration = useCallback((): FormFilterItemDescriptorType[] => {
    const fltConf: FormFilterItemDescriptorType[] = [
      {
        fieldName: 'azienda',
        placeHolder: 'Azienda',
        valueKeyName: 'id',
        valueDisplayExpr: 'nome',
        values: aziende.data.value,
        composeFilterItem(): any[] | null {
          if (this.currentValue === undefined || this.currentValue === null) return null
          return [['azienda/id', this.currentValue]]
        },
      },
    ]
    console.log('GENFLT - GETCONF :', fltConf)
    return fltConf
  }, [aziende])

  const initialFilterConfiguration = useMemo(() => getFiltersConfiguration(), [getFiltersConfiguration])
  const [filterConfiguration, setFilterConfiguration] = useState(initialFilterConfiguration)
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false)
  const getCurrentFilter = useCallback((): any[] => {
    let applied: boolean = false
    const currentFlts = []
    for (const flt of filterConfiguration) {
      const fltValue = flt.composeFilterItem()
      if (fltValue !== null) {
        currentFlts.push(fltValue)
      }
      if (!applied && flt.currentValue !== flt.defaultValue) applied = true
    }
    setFiltersApplied(applied)
    console.log('GENFLT - CURRENT:', currentFlts)
    return currentFlts
  }, [filterConfiguration])

  const [currentFilter, setCurrentFilter] = useState<any[]>(clientId ? [['azienda/id', Number(clientId)]] : [])

  const quotesDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/ad_quote`,
      key: 'id',
      keyType: 'Int64',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    expand: [
      'csn',
      'azienda($expand=agente($expand=commerciale_qs),rivenditore)',
      'contact',
      'admin',
      'commessa',
      'ad_quote_histories($expand=quote)',
      'ordini',
      'author',
      'ad_subscriptions',
    ],
    filter: currentFilter.length > 0 ? currentFilter : null,
    requireTotalCount: true,
  }

  const onCellPrepared = (e: CellPreparedEvent) => {
    if (e.rowType === 'data' && e.column.dataField === 'status' && e.value) {
      switch (e.value) {
        case 'ORDERED': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.EMERALD}`
          break
        }
        case 'UNDER REVIEW': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.YELLOW}`
          break
        }
        case 'QUOTED': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.CORNFLOWERBLUE}`
          break
        }

        case 'DRAFT': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.GREY}`
          break
        }
        case 'FINALIZING': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.VIOLET}`
          break
        }
        case 'EXPIRED': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: white; background-color: ${GridCellColor.BROWN}`
          break
        }
        case 'CANCELLED': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.SALMON}`
          break
        }
        case 'CANCELLING': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.ORANGE}`
          break
        }
        case 'FAILED': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: white; background-color: ${GridCellColor.RED}`
          break
        }
        case 'ORDER SUBMITTED': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.LILAC}`
          break
        }
      }
    }
  }

  const calculateOrdineCellValue = (rowData: ad_quote) => {
    return rowData.ordini?.[0]?.ded_Dis
  }

  const calculateOrdineFilterExpression = (
    filterValue: any,
    selectedFilterOperation: string | null,
    target: string,
  ): string | (() => any) | Array<any> => {
    console.log('FLT', filterValue, selectedFilterOperation, target)

    if (target === 'filterBuilder' || target === 'filterRow') {
      switch (selectedFilterOperation) {
        case '=': {
          return [[new EdmLiteral(`ordini/any(a: a/ded_Dis eq '${filterValue}')`), '=', true]]
        }
        case '<>': {
          return [[new EdmLiteral(`ordini/any(a: a/ded_Dis eq '${filterValue}')`), '=', false]]
        }
        case 'contains':
        case 'startswith':
        case 'endswith': {
          return [[new EdmLiteral(`ordini/any(a: ${selectedFilterOperation}(a/ded_Dis, '${filterValue}'))`), '=', true]]
        }
        default: {
          // caso notcontains
          return [[new EdmLiteral(`ordini/any(a: contains(a/ded_Dis, '${filterValue}'))`), '=', false]]
        }
      }
    } else if (target === 'search' || target === 'headerFilter') {
      return [[new EdmLiteral(`ordini/any(a: contains(a/ded_Dis, '${filterValue}'))`), '=', true]]
    }

    return filterValue
  }

  const getGridHeight = useCallback(() => {
    let h = '79vh'
    if (currentScreenSize.isMedium) {
      h = '75vh'
    } else if (currentScreenSize.isSmall) {
      h = '120vh'
    } else if (currentScreenSize.isXSmall) {
      h = '75vh'
    }
    return h
  }, [currentScreenSize])

  const onExporting = (e: ExportingEvent) => {
    ExportDataGridToExcel(e.component)
  }

  const ExportDataGridToExcel = (component: dxDataGrid) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('Quote')
    exportDataGrid({ component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Quotes.xlsx')
      })
    })
  }

  const onAnnullaClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: ad_quote) => {
      if (rowdata.status === 'DRAFT' || rowdata.status === 'QUOTED') {
        const result = confirm(`<i>Vuoi davvero annullare la quota <b>${rowdata.number}</b>?</i>`, 'Annullamento Quota')
        result.then((dialogResult) => {
          if (dialogResult === false) return
          fetcher.submit(
            { quoteNumber: rowdata.number },
            {
              method: 'put',
              action: `/quotes/${rowdata.id}/cancel`,
              encType: 'application/json',
            },
          )
        })
      } else {
        notify(`La quota non può essere annullata. Stato: '${rowdata.status}'`, 'warning', 3000)
      }
    })
  }

  const onFinalizzaClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: ad_quote) => {
      if (rowdata.status === 'DRAFT') {
        const result = confirm(
          `<i>Vuoi davvero finalizzare la quota <b>${rowdata.number}</b>?</i>`,
          'Finalizzazione Quota',
        )
        result.then((dialogResult) => {
          if (dialogResult === false) return
          fetcher.submit(
            { quoteNumber: rowdata.number },
            {
              method: 'put',
              action: `/quotes/${rowdata.id}/finalize`,
              encType: 'application/json',
            },
          )
        })
      } else {
        notify(`La quota non può essere finalizzata. Stato: '${rowdata.status}'`, 'warning', 3000)
      }
    })
  }

  const onAggiornaStatoClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, async (rowdata: ad_quote) => {
      if (rowdata.status === 'FAILED' || rowdata.status === 'CANCELLED') {
        notify(`La quota non può essere aggiornata. Stato: '${rowdata.status}'`, 'warning', 3000)
      } else {
        gridRef?.current?.instance().beginCustomLoading('Aggiornamento stato in corso....')
        await client
          .put(
            '/api/autodeskpws/quotes/status',
            { quoteNumber: rowdata.number, transactionId: rowdata.transactionId },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            },
          )
          .then((res: AxiosResponse<any, any>) => {
            console.log('res', res)
            if (res.status === 204) notify('Nessun aggiornamento disponibile.', 'warning', 5000)
            else if ('quoteStatus' in res.data && res.data.quoteStatus === 'FAILED') {
              notify(
                `Quota ${rowdata.transactionId} aggiornata correttamente: stato '${res.data.quoteStatus}'`,
                'success',
                5000,
              )
            } else {
              notify(
                `Quota ${res.data[0].quoteNumber} aggiornata correttamente: stato '${res.data[0].quoteStatus.toUpperCase()}'`,
                'success',
                5000,
              )
            }
          })
          .catch((error: AxiosError<any, any>) => {
            notify(`Errore aggiornamento stato: ${error}`, 'error', 5000)
          })
          .finally(() => {
            gridRef.current?.instance().endCustomLoading()
            gridRef.current?.instance().refresh()
          })
      }
    })
  }

  const onDuplicaClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, async (rowdata: ad_quote) => {
      gridRef?.current?.instance().beginCustomLoading('Caricamento nuova quota....')
      navigate(`new?fromQuote=${rowdata.id}`)
    })
  }

  const onCreaRinnoviClick = (e: ClickEvent) => {
    const result = confirm(
      `<i>Vuoi davvero creare i rinnovi? L'operazione potrebbe durare qualche minuto</i>`,
      'Rinnovi automatici',
    )
    result.then((dialogResult) => {
      if (dialogResult === false) return
      fetcher.submit(
        {},
        {
          method: 'post',
          action: `/quotes/autorenewalcreate`,
          encType: 'application/json',
        },
      )
    })
  }

  const onEsportaPDFClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, async (rowdata: ad_quote) => {
      if (rowdata.number && rowdata.number !== 'NA') {
        const result = confirm(
          `<i>Vuoi davvero esportare il PDF per la quota <b>${rowdata.number}</b>?</i>`,
          'Esporta PDF',
        )
        result.then((dialogResult) => {
          if (dialogResult === false) return
          gridRef?.current?.instance().beginCustomLoading('Esportazione pdf....')
          exportQuotePdf(client, rowdata.number!)
            .catch((error: AxiosError) => {
              notify(`Errore esportazione documento. Dettagli : ${error}`, 'error', 3000)
            })
            .finally(() => {
              gridRef?.current?.instance().endCustomLoading()
            })
        })
      } else notify('Selezionare una quota con numerazione valida', 'warning', 3000)
    })
  }

  const onRefreshClick = (e: ClickEvent) => {
    gridRef.current?.instance().refresh()
  }

  const onDetailsClick = (status: string, transactionId: string, quoteNumber: string, id: number) => {
    gridRef?.current?.instance().beginCustomLoading('Recupero dettagli quota in corso....')
    navigate(`${id}`)
  }

  useEffect(() => {
    //nel fetcher.data viene memorizzato il risultato dell'ultima azione eseguita (si puo usare anche useActionData con useSubmit)
    if (fetcher.data && fetcher.state === 'idle') {
      console.log(fetcher.data)
      gridRef.current?.instance().endCustomLoading()
      gridRef.current?.instance().refresh()
    } else if (fetcher.state === 'submitting') {
      gridRef?.current?.instance().beginCustomLoading('Aggiornamento in corso....')
    }
  }, [fetcher.data, fetcher.state])

  useEffect(() => {
    getCurrentFilter()
  }, [])

  const onCommissionsClick = (e: ClickEvent) => {
    commissionsPopupRef.current?.instance().show()
  }

  return (
    <>
      {!clientId && <h2 className={'content-block'}>{title}</h2>}
      <div className={clientId ? '' : 'content-block'}>
        {!clientId && (
          <div className="accordion-generic-filter-form">
            <Accordion
              className={filtersApplied ? 'with-filter' : ''}
              collapsible={true}
              onContentReady={(e: ContentReadyEvent<any, any>) => {
                e.component?.collapseItem(0)
              }}
            >
              <AccordionItem icon="filter" title={'Filtri'}>
                <GenericFilterForm
                  FormItemDescriptors={filterConfiguration}
                  resetFilterClicked={function (e: ClickEvent): void {
                    setFilterConfiguration(getFiltersConfiguration())
                  }}
                  filterValueChanged={function (): void {
                    const currentFlts = getCurrentFilter()
                    console.log('GENFLT - VALUE CHNG', currentFlts)
                    if (JSON.stringify(currentFlts) !== JSON.stringify(currentFilter)) {
                      console.log('GENFLT - SETTING FILT', currentFlts)
                      setCurrentFilter(currentFlts)
                    }
                  }}
                />
              </AccordionItem>
            </Accordion>
          </div>
        )}
        <DataGrid
          id={clientId ? 'client-quotes-datagrid' : 'quotes-datagrid'}
          height={getGridHeight()}
          dataSource={quotesDataSource}
          noDataText="Nessuna quota trovata"
          className={'dx-card wide-card'}
          showBorders={false}
          showColumnLines={true}
          wordWrapEnabled={false}
          showRowLines={true}
          focusedRowEnabled={true}
          rowAlternationEnabled={true}
          allowColumnResizing={true}
          allowColumnReordering={true}
          width="100%"
          onCellPrepared={onCellPrepared}
          onExporting={onExporting}
          ref={gridRef}
          columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
          remoteOperations={{
            filtering: true,
            grouping: false,
            groupPaging: false,
            paging: true,
            sorting: true,
            summary: true,
          }}
          grouping={{
            // Fondamentale per il raggruppamento veloce!!!
            autoExpandAll: true,
          }}
          groupPanel={{
            visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
            emptyPanelText: 'Trascina qui una colonna per raggruppare',
          }}
          onRowDblClick={(e: RowDblClickEvent<any, any>) => {
            if (e.rowType === 'data') onDetailsClick(e.data.status, e.data.transactionId, e.data.number, e.data.id)
          }}
        >
          <Pager
            visible={true}
            allowedPageSizes={'auto'}
            displayMode={'compact'}
            showPageSizeSelector={false}
            showInfo={true}
            showNavigationButtons={false}
            infoText={gridTotalItemsLabel}
          />
          <FilterRow visible={true} />
          <SearchPanel visible={true} width={currentScreenSize.isXSmall ? 180 : 250} />
          <Sorting mode="multiple"></Sorting>
          <Export enabled={true} />
          <FilterPanel visible={true} />
          <Editing allowAdding={true} />
          <HeaderFilter visible={true} />
          <Selection mode="single" />
          <ColumnChooser enabled={true} />
          <Scrolling mode="virtual" />
          <StateStoring
            enabled={true}
            type="localStorage"
            storageKey={clientId ? 'client-quotes-datagrid' : 'quotes-datagrid'}
            savingTimeout={50}
          />
          <MasterDetail enabled={true} component={QuoteBriefDetail} />,
          <Column
            visible={false}
            type="buttons"
            width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
            alignment="left"
            showInColumnChooser={false}
          >
            <GridButton
              hint="Details"
              icon="search"
              onClick={(e: ColumnButtonClickEvent) => {
                navigate(`${e.row?.data.id}`)
              }}
            ></GridButton>
          </Column>
          <Column
            dataField={'id'}
            width={'5%'}
            caption="ID"
            visible={false}
            dataType="number"
            defaultSortOrder="desc"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
            }
          >
            {!clientId && <HeaderFilter dataSource={columnSourceFactory(token, 'ad_quote', 'id', true)}></HeaderFilter>}
          </Column>
          <Column
            dataField="transactionId"
            visible={true}
            caption="ID TRANSACTION"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: ad_quote = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`${data.id}`}
                  text={data.transactionId?.toString()}
                  disabled={
                    !(isUserRoleAllowed(userInfo?.roles, [Roles.Guest]) && (!data.number || data.number === 'NA'))
                  }
                ></ObjectCodeLink>
              )
            }}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 18 : undefined
            }
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'ad_quote', 'transactionId')}>
                <Search enabled={true} searchExpr={'transactionId'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="number"
            caption="NUMBER"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: ad_quote = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`${data.id}`}
                  text={data.number}
                  disabled={
                    !(
                      isUserRoleAllowed(userInfo?.roles, [Roles.Guest]) &&
                      data.number !== null &&
                      data.number !== undefined &&
                      data.number !== 'NA'
                    )
                  }
                ></ObjectCodeLink>
              )
            }}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 19 : undefined
            }
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'ad_quote', 'number')}>
                <Search enabled={true} searchExpr={'number'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="azienda.nome"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: ad_quote = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`/clients/${data.azienda?.id}`}
                  text={data.azienda?.nome}
                  disabled={clientId ? true : !isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
                ></ObjectCodeLink>
              )
            }}
            caption="AZIENDA"
            visible={clientId === undefined}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 21 : undefined
            }
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'ad_quote', 'azienda.nome')}>
                <Search enabled={true} searchExpr={'azienda.nome'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="azienda.agente.commerciale_qs.fullname"
            caption="AGENTE"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter
                dataSource={columnSourceFactory(token, 'ad_quote', 'azienda.agente.commerciale_qs.fullname')}
              >
                <Search enabled={true} searchExpr={'azienda.agente.commerciale_qs.fullname'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField={'azienda.rivenditore.nome'}
            caption={'RIVENDITORE'}
            visible={false}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'Azienda', 'rivenditore.nome')}>
                <Search enabled={true} searchExpr={'rivenditore.nome'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="csn.csn"
            caption="CSN"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 20 : undefined
            }
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 90 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 90 : undefined}
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'ad_quote', 'csn.csn')}>
                <Search enabled={true} searchExpr={'csn.csn'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="status"
            caption="STATO"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 17 : undefined
            }
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'ad_quote', 'status')}>
                <Search enabled={true} searchExpr={'status'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="contact.fullname"
            caption="CONTATTO"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 16 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'ad_quote', 'contact.fullname')}>
                <Search enabled={true} searchExpr={'contact.fullname'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="admin.fullname"
            caption="AMMINISTRATORE"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 15 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'ad_quote', 'admin.fullname')}>
                <Search enabled={true} searchExpr={'admin.fullname'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="author.fullname"
            caption="AUTORE"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 14 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'ad_quote', 'author.fullname')}>
                <Search enabled={true} searchExpr={'author.fullname'} />
              </HeaderFilter>
            )}
          </Column>
          {DateColumn({
            dataField: 'creationDate',
            caption: 'CREAZIONE',
            format: 'dd/MM/yyyy',
            hidingPriority:
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 13 : undefined,
          })}
          {DateColumn({
            dataField: 'expiringDate',
            caption: 'SCADENZA',
            format: 'dd/MM/yyyy',
            hidingPriority:
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 12 : undefined,
          })}
          {DateColumn({
            dataField: 'orderedDate',
            caption: 'ORDINATA',
            format: 'dd/MM/yyyy',
            visible: false,
            hidingPriority:
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 12 : undefined,
          })}
          <Column
            name="info_ordine"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: ad_quote = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`/orders/${data.ordini?.[0]?.id}`}
                  text={data.ordini?.[0]?.ded_Dis}
                  disabled={!isUserRoleAllowed(userInfo?.roles, [...defaultCRUDAllowedRoles, Roles.Marketing])}
                ></ObjectCodeLink>
              )
            }}
            caption="ORDINE"
            calculateCellValue={calculateOrdineCellValue}
            calculateFilterExpression={calculateOrdineFilterExpression}
            allowFiltering={true}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 11 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'ordine', 'ded_Dis', true)}>
                <Search enabled={true} searchExpr={'ded_Dis'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="commessa.ded_Dis"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: ad_quote = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`/projects/${data.commessa?.id}`}
                  text={data.commessa?.ded_Dis}
                  disabled={!isUserRoleAllowed(userInfo?.roles, [...defaultCRUDAllowedRoles, Roles.Marketing])}
                ></ObjectCodeLink>
              )
            }}
            caption="COMMESSA"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 10 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'ad_quote', 'commessa.ded_Dis', true)}>
                <Search enabled={true} searchExpr={'commessa.ded_Dis'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="auto"
            caption="AUTOMATICA"
            allowHeaderFiltering={false}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 9 : undefined
            }
          ></Column>
          <Column
            dataField="attivitaTecnicaRichiesta"
            caption="ATTIVITA' RICHIESTA"
            allowHeaderFiltering={false}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 8 : undefined
            }
          ></Column>
          {DateColumn({
            dataField: 'autoFinalizationDate',
            caption: 'FINALIZZAZIONE AUTOMATICA',
            format: 'dd/MM/yyyy',
            visible: false,
            hidingPriority:
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined,
          })}
          {DateColumn({
            dataField: 'autoExpiringDate',
            caption: 'SCADENZA AUTOMATICA',
            format: 'dd/MM/yyyy',
            visible: false,
            hidingPriority:
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined,
          })}
          <Toolbar>
            <Item
              location="before"
              locateInMenu="never"
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Refresh',
                text: 'Refresh',
                icon: 'refresh',
                onClick: onRefreshClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item name="groupPanel" />
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              name="addRowButton"
              options={{
                onClick: () => {
                  gridRef?.current?.instance().beginCustomLoading('Caricamento nuova quota....')
                  navigate(`new`)
                },
                hint: 'Nuova quota',
                text: 'Nuova quota',
              }}
            />
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Sales,
              ])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Crea rinnovi',
                text: 'Crea rinnovi',
                icon: 'money',
                onClick: onCreaRinnoviClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Duplica',
                text: 'Duplica',
                icon: 'copy',
                onClick: onDuplicaClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Aggiorna stato',
                text: 'Aggiorna stato',
                icon: 'pulldown',
                onClick: onAggiornaStatoClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Finalizza quota',
                text: 'Finalizza quota',
                icon: 'check',
                onClick: onFinalizzaClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Annulla quota',
                text: 'Annulla quota',
                icon: 'remove',
                onClick: onAnnullaClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Esporta .PDF',
                text: 'Esporta .PDF',
                icon: 'exportpdf',
                onClick: onEsportaPDFClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.Administrator])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Commissioni',
                text: 'Commissioni',
                icon: 'group',
                onClick: onCommissionsClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              name="columnChooserButton"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
            />
            <Item
              name="exportButton"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              options={{ hint: 'Esporta dati in excel', text: 'Esporta dati in excel' }}
            />
            <Item name="searchPanel" />
          </Toolbar>
        </DataGrid>
        <CommissionsSelector popupRef={commissionsPopupRef} />
      </div>
    </>
  )
}

export default QuotesPage
